import type { MediaUsageReference } from '../dto/media/media.res'

/**
 * The image usages the code renders against by name.
 *
 * Usages are data — an operator adds a fourth from the system area without a
 * deployment. These three are different: a component asks for one by key, so
 * renaming or deleting one silently drops every surface that names it back to
 * the legacy size scheme, which is also a legitimate state during a reprocess
 * and therefore indistinguishable from working.
 *
 * `ImageUsagesService` refuses to rename or delete a key in this list, the
 * same protection `PROTECTED_FORMULAS` gives the expressions the app reads.
 *
 * Three, not one per surface: a usage is a *rendition* — a width, a shape and
 * a crop rule — and two surfaces asking for the same rendition were rendering,
 * storing and re-rendering the same pixels under two names.
 */
const CODE_IMAGE_USAGES = ['PREVIEW', 'DETAIL', 'ICON'] as const

export type CodeImageUsageKey = (typeof CODE_IMAGE_USAGES)[number]

export function isCodeImageUsage(key: string): key is CodeImageUsageKey {
  return (CODE_IMAGE_USAGES as readonly string[]).includes(key)
}

/**
 * The small rendition every list draws: a configurator card, the media
 * library's grid and list, an image picker.
 *
 * Landscape and cropped around the focal point, because the configurator card
 * is the surface with a fixed shape. A square tile takes the middle of it.
 */
export const PREVIEW_USAGE: CodeImageUsageKey = 'PREVIEW'

/** The large rendition — a measure's full-size view, a product's preview
 *  panel. The picture's own shape, only bounded in width. */
export const DETAIL_USAGE: CodeImageUsageKey = 'DETAIL'

/** The smallest rendition, square: a product beside its name in a list. */
export const ICON_USAGE: CodeImageUsageKey = 'ICON'

/** Where a picture of an entry can be pointed at. Checked against
 *  `MediaUsageReference['kind']`, which also names places that are not one —
 *  a kind added there and forgotten here would silently stop being a picture
 *  and vanish from the resolution report. */
const IMAGE_SLOTS = [
  'MEASURE_IMAGE',
  'PRODUCT_IMAGE',
] as const satisfies readonly MediaUsageReference['kind'][]

/**
 * Every rendition a picture of a measure or a product can be drawn at.
 *
 * The one answer to "is this picture big enough", read by the picker that
 * warns before an image is chosen and by the report that lists the ones
 * already in place.
 *
 * All three, and one list rather than one per entity: the picker cannot know
 * which role a file is being chosen for, and a picture moved from the gallery
 * to the card slot is drawn at renditions the gallery never asked of it.
 */
export const ENTRY_IMAGE_USAGES: readonly CodeImageUsageKey[] =
  CODE_IMAGE_USAGES

/** Whether any of `slots` is a place a picture of an entry is drawn. */
export function isEntryPicture(slots: readonly string[]): boolean {
  return slots.some(slot => (IMAGE_SLOTS as readonly string[]).includes(slot))
}
